import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface NavLink {
  label: string;
  href: string;
}

const navLinks: NavLink[] = [
  { label: 'Home', href: '/' },
  { label: 'Sell Your Home', href: '/sell-your-home' },
  { label: 'Home Valuation', href: '/home-valuation' },
  { label: 'About Carol', href: '/about' },
  { label: 'Seller Resources', href: '/seller-resources' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState<any>(null);
  const location = useLocation();

  // Check auth state
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const isAdminPage = location.pathname.startsWith('/admin');

  // Don't show public navbar on admin pages
  if (isAdminPage) return null;

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50 shadow-sm'
          : 'bg-foreground-950'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between py-3">
          {/* Logo / Name */}
          <Link to="/" className="flex-shrink-0">
            <h1
              className={`font-heading text-base md:text-lg tracking-wide leading-tight transition-colors duration-300 ${
                scrolled ? 'text-foreground-900' : 'text-accent-100'
              }`}
            >
              CAROL L BERRY
              <span
                className={`block text-xs md:text-sm font-body tracking-widest uppercase transition-colors duration-300 ${
                  scrolled ? 'text-foreground-500' : 'text-accent-300'
                }`}
              >
                Real Estate Professional
              </span>
            </h1>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`relative px-3 py-2 text-sm font-body tracking-wide whitespace-nowrap rounded-md transition-all duration-200 ${
                  isActive(link.href)
                    ? scrolled
                      ? 'text-primary-600 font-semibold'
                      : 'text-accent-100 font-semibold'
                    : scrolled
                      ? 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                      : 'text-accent-300 hover:text-accent-50 hover:bg-foreground-800/50'
                }`}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-primary-500 rounded-full" />
                )}
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <Link
              to="/home-valuation"
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-4 py-2.5 rounded-md text-sm font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-all duration-200"
            >
              <i className="ri-home-4-line text-base" />
              Request a Home Valuation
            </Link>
            {/* Blog Edit Button */}
            <span className={`w-px h-6 mx-1 transition-colors duration-300 ${scrolled ? 'bg-foreground-200' : 'bg-foreground-700/50'}`} />
            <Link
              to="/admin/login"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-body font-medium whitespace-nowrap transition-all duration-200"
              title="Edit Blog"
            >
              <i className={`text-base transition-colors duration-300 ${
                scrolled ? 'ri-edit-line text-foreground-500' : 'ri-edit-line text-accent-300'
              }`} />
              <span className={`transition-colors duration-300 ${scrolled ? 'text-foreground-600 hover:text-foreground-900' : 'text-accent-300 hover:text-accent-100'}`}>
                Edit Blog
              </span>
            </Link>
            {user && (
              <Link
                to="/admin"
                className="w-9 h-9 flex items-center justify-center rounded-md transition-colors duration-300"
                title="Admin"
              >
                <i className={`text-lg transition-colors duration-300 ${scrolled ? 'ri-settings-3-line text-foreground-500 hover:text-foreground-700' : 'ri-settings-3-line text-accent-300 hover:text-accent-100'}`} />
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors duration-300"
            aria-label="Toggle menu"
          >
            <i
              className={`text-xl transition-colors duration-300 ${
                mobileOpen
                  ? 'ri-close-line text-foreground-900'
                  : scrolled
                    ? 'ri-menu-line text-foreground-700'
                    : 'ri-menu-line text-accent-200'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200">
          <nav className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-3 py-2.5 rounded-md text-sm font-body tracking-wide transition-colors ${
                  isActive(link.href)
                    ? 'bg-primary-50 text-primary-700 font-semibold'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/home-valuation"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-4 py-3 rounded-md text-sm font-body font-semibold hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-4-line text-base" />
              Request a Home Valuation
            </Link>
            <Link
              to="/admin/login"
              className="mt-1 inline-flex items-center justify-center gap-2 bg-background-100 text-foreground-700 px-4 py-3 rounded-md text-sm font-body font-semibold hover:bg-background-200 transition-colors"
            >
              <i className="ri-edit-line text-base" />
              Edit Blog
            </Link>
            {user && (
              <Link
                to="/admin"
                className="mt-1 inline-flex items-center justify-center gap-2 bg-background-100 text-foreground-700 px-4 py-3 rounded-md text-sm font-body font-semibold hover:bg-background-200 transition-colors"
              >
                <i className="ri-settings-3-line text-base" />
                Blog Admin
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}